import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Zap, RotateCcw, History, Star, AlertCircle, ChevronDown, Check, Info, Download, FileText, Loader2, ScrollText, X } from "lucide-react";
import TopNav from "../components/TopNav";
import { ContinuityPanel } from "../components/ContinuityPanel";
import { WriterLoader } from "../components/WriterLoader";
import { ForgeAnimation } from "../components/ForgeAnimation";
import { DemoProvider } from "../contexts/DemoContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Toaster, toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CE_COLORS = {
  "CE-01": "#C0392B", "CE-02": "#F39C12", "CE-03": "#3498DB",
  "CE-04": "#E74C3C", "CE-05": "var(--semantic-success)", "CE-06": "var(--ce-05-wonder)",
  "CE-07": "#2C3E50", "CE-08": "var(--accent-gold)", "CE-09": "#1ABC9C",
};
const CE_DATA = [
  { code: "CE-01", name: "Romance", desc: "Desire, attraction, longing" },
  { code: "CE-02", name: "Joy", desc: "Humor, delight, playfulness" },
  { code: "CE-03", name: "Sorrow", desc: "Grief, loss, heartbreak" },
  { code: "CE-04", name: "Fury", desc: "Anger, outrage, betrayal" },
  { code: "CE-05", name: "Valor", desc: "Courage, heroism, triumph" },
  { code: "CE-06", name: "Dread", desc: "Fear, terror, suspense" },
  { code: "CE-07", name: "Revulsion", desc: "Disgust, moral rejection" },
  { code: "CE-08", name: "Wonder", desc: "Awe, curiosity, revelation" },
  { code: "CE-09", name: "Serenity", desc: "Peace, acceptance, calm" },
];

const MODEL_OPTIONS = [
  { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", cost: 3, badge: "Recommended", badgeColor: "var(--semantic-success)" },
  { id: "claude-opus-4-6", name: "Claude Opus 4.6", cost: 8, badge: "Premium", badgeColor: "var(--ce-05-wonder)" },
  { id: "gpt-4o", name: "GPT-4o", cost: 2, badge: "Fast", badgeColor: "var(--accent-gold)" },
  { id: "gpt-5-4", name: "GPT-5.4", cost: 7, badge: "Premium", badgeColor: "var(--ce-05-wonder)" },
  { id: "gemini-3-pro", name: "Gemini 3 Pro", cost: 4, badge: "Multilingual", badgeColor: "var(--accent-gold)" },
];

const SCENE_TYPE_OPTIONS = [
  { value: "confrontation", label: "Confrontation — active tension between characters" },
  { value: "solitude", label: "Solitude — character alone, processing or deciding" },
  { value: "discovery", label: "Discovery — reveal that recontextualizes the story" },
  { value: "decision", label: "Decision — character commits to a course of action" },
  { value: "transition", label: "Transition — bridge scene carrying emotional residue" },
];

function ScoreDisplay({ score, feedback }) {
  if (!score && score !== 0) return null;
  const normalizedScore = score > 10 ? score / 10 : score;
  const displayScore = Number(normalizedScore).toFixed(1);
  const cls = normalizedScore >= 7.5 ? "score-high" : normalizedScore >= 6 ? "score-mid" : "score-low";
  return (
    <div className="flex items-center gap-3 mt-4 p-3 rounded-xl" style={{ background: "var(--surface-page)" }}>
      <div className={`text-2xl font-black px-3 py-1 rounded-lg ${cls}`}>{displayScore}</div>
      <div>
        <div className="text-[var(--ink-secondary)] text-xs font-medium mb-0.5">Emotional Score</div>
        {feedback && <div className="text-[var(--ink-tertiary)] text-xs">{feedback}</div>}
      </div>
    </div>
  );
}

function HistoryModal({ sceneId, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/scenes/${sceneId}/history`, { withCredentials: true })
      .then(r => setHistory(r.data))
      .finally(() => setLoading(false));
  }, [sceneId]);

  const restore = async (genId) => {
    await axios.post(`${API}/scenes/${sceneId}/history/restore`, { generation_id: genId }, { withCredentials: true });
    onClose(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => onClose(false)} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-2xl z-10"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <h3 className="font-bold">Generation History</h3>
          <button onClick={() => onClose(false)} className="text-[var(--ink-tertiary)] hover:">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {loading ? <div className="text-center text-[var(--ink-tertiary)] py-8">Loading...</div> :
            history.length === 0 ? <div className="text-center text-[var(--ink-tertiary)] py-8">No history yet</div> :
            history.map(g => (
              <div key={g.id} className="rounded-xl p-4" style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-[var(--ink-secondary)] text-xs font-mono">{new Date(g.created_at).toLocaleString()}</span>
                    <span className="text-[var(--ink-tertiary)] text-xs">{g.model}</span>
                    {g.emotional_score && <span className="text-xs font-bold score-high">{g.emotional_score}</span>}
                    <span className="text-[var(--ink-tertiary)] text-xs">{g.credits_consumed}cr</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setExpanded(expanded === g.id ? null : g.id)}
                      className="text-[var(--ink-tertiary)] hover: text-xs px-2 py-1 rounded-lg transition-colors"
                      style={{ background: "var(--surface-page)" }}>
                      {expanded === g.id ? "Collapse" : "Expand"}
                    </button>
                    <button onClick={() => restore(g.id)} data-testid="restore-generation-button"
                      className="text-xs px-3 py-1 rounded-lg transition-colors"
                      style={{ background: "var(--accent-gold)" }}>
                      Restore
                    </button>
                  </div>
                </div>
                {expanded === g.id && (
                  <pre className="screenplay-font text-[var(--ink-secondary)] text-xs mt-3 whitespace-pre-wrap overflow-auto max-h-48">
                    {g.output_script}
                  </pre>
                )}
              </div>
            ))
          }
        </div>
      </motion.div>
    </div>
  );
}

function ExportMenu({ disabled, exporting, onExport, testIdPrefix }) {
  const options = [
    { format: "docx", label: "Word Document (.docx)", icon: FileText },
    { format: "pdf", label: "Screenplay PDF (.pdf)", icon: Download },
    { format: "fountain", label: "Fountain (.fountain)", icon: ScrollText },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || exporting}
          aria-label="Open export menu"
          data-testid={`${testIdPrefix}-trigger`}
          className="h-14 min-w-[11rem] rounded-xl border-[var(--border-subtle)] bg-[var(--surface-card)] text-[var(--ink-primary)] hover:bg-[var(--surface-raised)] hover:"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "Exporting..." : "Export"}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 border-[var(--border-subtle)] bg-[var(--surface-card)]"
        data-testid={`${testIdPrefix}-content`}
      >
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              key={option.format}
              onClick={() => onExport(option.format)}
              data-testid={`${testIdPrefix}-${option.format}`}
              className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[var(--ink-primary)] focus:bg-[var(--surface-raised)] focus:"
            >
              <Icon className="h-4 w-4 text-[var(--accent-gold)]" />
              {option.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SaveWorkModal({ userEmail, onSave, onSkip }) {
  const [email, setEmail] = useState(userEmail || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/demo-upgrade`, { email, password }, { withCredentials: true });
      onSave();
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="save-work-modal">
      <div className="absolute inset-0" style={{ background: "rgba(245,238,225,0.75)", backdropFilter: "blur(6px)" }} />
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-2xl p-7 z-10"
        style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }}
      >
        <button onClick={onSkip} data-testid="save-modal-skip-x"
          className="absolute top-4 right-4 text-[var(--ink-tertiary)] hover:text-[var(--ink-secondary)] transition-colors">
          <X size={18} />
        </button>

        <h2 className="text-xl font-bold mb-1">Save Your Work</h2>
        <p className="text-[var(--ink-secondary)] text-sm mb-5">
          Create a free account to access your scripts anytime, from any device.
        </p>

        <form onSubmit={handleSave} className="space-y-3">
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Your email address" required data-testid="save-modal-email"
            className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
            style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }}
          />
          <input
            type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Choose a password" required data-testid="save-modal-password"
            className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
            style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }}
          />
          <input
            type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm password" required data-testid="save-modal-confirm"
            className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
            style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }}
          />
          {error && (
            <p className="text-xs rounded-lg px-3 py-2" style={{ background: "rgba(184,117,107,0.1)", color: "var(--semantic-error)" }}>
              {error}
            </p>
          )}
          <button type="submit" disabled={saving} data-testid="save-modal-submit"
            className="w-full py-3 rounded-xl font-bold text-sm transition-all mt-1"
            style={{ background: "var(--accent-gold)", color: "var(--surface-page)", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving..." : "Save Account"}
          </button>
        </form>

        <button onClick={onSkip} data-testid="save-modal-skip"
          className="w-full text-center text-[var(--ink-tertiary)] text-sm mt-3 hover:text-[var(--ink-secondary)] transition-colors">
          Skip for now
        </button>
      </motion.div>
    </div>
  );
}

export default function SceneEditorPage() {
  const { projectId, episodeId, sceneId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [scene, setScene] = useState(null);
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sceneBrief, setSceneBrief] = useState("");

  // Emotion panel state
  const [selectedCE, setSelectedCE] = useState("CE-01");
  const [secondaryCE, setSecondaryCE] = useState("");
  const [sceneType, setSceneType] = useState("");
  const [selectedTS, setSelectedTS] = useState([]);
  const [selectedIR, setSelectedIR] = useState([]);
  const [tsOptions, setTsOptions] = useState([]);
  const [irOptions, setIrOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState("claude-sonnet-4-6");
  const [additionalDirection, setAdditionalDirection] = useState("");

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedScript, setGeneratedScript] = useState("");
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [genError, setGenError] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [generationStatus, setGenerationStatus] = useState("");
  const [sceneInitialized, setSceneInitialized] = useState(false);

  // Save-work modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const shownSaveModalRef = useRef(false);

  // UI state
  const [showHistory, setShowHistory] = useState(false);
  const [balance, setBalance] = useState(100);
  const [editingScript, setEditingScript] = useState(false);
  const [editedScript, setEditedScript] = useState("");
  const [exportingFormat, setExportingFormat] = useState("");
  const [project, setProject] = useState(null);
  const scriptRef = useRef(null);

  const parseExportError = async (error) => {
    const payload = error?.response?.data;
    if (payload instanceof Blob) {
      try {
        const text = await payload.text();
        const json = JSON.parse(text);
        return json.detail || "Export failed";
      } catch {
        return "Export failed";
      }
    }
    return payload?.detail || error?.message || "Export failed";
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const deriveFilename = (headers, fallback) => {
    const disposition = headers?.["content-disposition"] || headers?.get?.("content-disposition");
    const match = disposition?.match(/filename=([^;]+)/i);
    return match ? match[1].replace(/"/g, "") : fallback;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, eRes, projRes] = await Promise.all([
          axios.get(`${API}/scenes/${sceneId}`, { withCredentials: true }),
          axios.get(`${API}/projects/${projectId}/episodes/${episodeId}`, { withCredentials: true }),
          axios.get(`${API}/projects/${projectId}`, { withCredentials: true }),
        ]);
        const s = sRes.data;
        setScene(s);
        setEpisode(eRes.data);
        setProject(projRes.data);
        setSceneBrief(s.brief || s.context || "");
        setSelectedCE(s.primary_ce || "CE-01");
        setSecondaryCE(s.secondary_ce || "");
        setSceneType(s.scene_type || "");
        setSelectedTS(s.selected_ts || []);
        setSelectedIR(s.selected_ir || []);
        setGeneratedScript(s.generated_script || "");
        setScore(s.emotional_score);
        setSceneInitialized(true);
        // Load balance
        const bRes = await axios.get(`${API}/credits/balance`, { withCredentials: true });
        setBalance(bRes.data.balance);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [sceneId, episodeId, projectId]);

  useEffect(() => {
    if (!selectedCE) return;
    axios.get(`${API}/arcengine/taxonomy/states/${selectedCE}`, { withCredentials: true })
      .then(r => setTsOptions(r.data)).catch(() => setTsOptions([]));
    axios.get(`${API}/arcengine/taxonomy/responses`, { withCredentials: true })
      .then(r => setIrOptions(r.data)).catch(() => setIrOptions([]));
  }, [selectedCE]);

  useEffect(() => {
    if (!sceneInitialized || !selectedCE || sceneType) return;
    axios.get(`${API}/suggest-scene-type`, {
      params: { ce: selectedCE, secondary_ce: secondaryCE || undefined },
      withCredentials: true,
    }).then((response) => {
      const suggested = response.data?.suggested_scene_type;
      if (suggested) {
        setSceneType(suggested);
        axios.patch(`${API}/scenes/${sceneId}`, { scene_type: suggested }, { withCredentials: true });
      }
    }).catch(() => {});
  }, [selectedCE, secondaryCE, sceneType, sceneId, sceneInitialized]);

  const handleCESelect = (code) => {
    setSelectedCE(code);
    if (secondaryCE === code) {
      setSecondaryCE("");
    }
    setSceneType("");
    setSelectedTS([]);
    // Save to backend
    axios.patch(`${API}/scenes/${sceneId}`, {
      primary_ce: code,
      secondary_ce: secondaryCE === code ? "" : secondaryCE || "",
      scene_type: "",
      selected_ts: [],
    }, { withCredentials: true });
  };

  const handleSecondaryCEChange = (value) => {
    const nextValue = value || "";
    setSecondaryCE(nextValue);
    setSceneType("");
    axios.patch(`${API}/scenes/${sceneId}`, { secondary_ce: nextValue, scene_type: "" }, { withCredentials: true });
  };

  const handleSceneTypeChange = (value) => {
    setSceneType(value);
    axios.patch(`${API}/scenes/${sceneId}`, { scene_type: value }, { withCredentials: true });
  };

  const saveSceneBrief = async (nextBrief) => {
    const trimmed = nextBrief.trim();
    setSceneBrief(nextBrief);
    setScene((prev) => prev ? { ...prev, brief: trimmed, context: trimmed } : prev);
    try {
      await axios.patch(`${API}/scenes/${sceneId}`, { brief: trimmed }, { withCredentials: true });
    } catch (err) {
      console.error(err);
    }
  };

  const toggleTS = (code) => {
    const updated = selectedTS.includes(code)
      ? selectedTS.filter(c => c !== code)
      : selectedTS.length < 3 ? [...selectedTS, code] : selectedTS;
    setSelectedTS(updated);
    axios.patch(`${API}/scenes/${sceneId}`, { selected_ts: updated }, { withCredentials: true });
  };

  const toggleIR = (code) => {
    const updated = selectedIR.includes(code)
      ? selectedIR.filter(c => c !== code)
      : selectedIR.length < 3 ? [...selectedIR, code] : selectedIR;
    setSelectedIR(updated);
    axios.patch(`${API}/scenes/${sceneId}`, { selected_ir: updated }, { withCredentials: true });
  };

  const handleGenerate = async () => {
    if (generating) return;
    if (!sceneBrief.trim()) {
      setGenError("Please add a Scene Brief before generating.");
      return;
    }
    if (!sceneType) {
      setGenError("Please choose a scene type before generating.");
      return;
    }
    await saveSceneBrief(sceneBrief);
    setGenerating(true);
    setStreaming(true);
    setGeneratedScript("");
    setScore(null);
    setFeedback("");
    setGenError("");
    setGenerationStatus("ArcEngine is drafting your scene...");

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/arcengine/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          scene_id: sceneId,
          model: selectedModel,
          use_byok: false,
          additional_direction: additionalDirection || null,
          secondary_ce: secondaryCE || null,
          scene_type: sceneType || null,
          force: false,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullScript = "";
      let completedNormally = false;
      let lastChunkAt = Date.now();

      // 10-second fallback: if stream goes quiet after tokens arrive, auto-complete
      const fallbackTimer = setTimeout(async () => {
        if (!completedNormally && fullScript) {
          try {
            const r = await axios.get(`${API}/scenes/${sceneId}`, { withCredentials: true });
            if (r.data.emotional_score) setScore(r.data.emotional_score);
            setGenerationStatus("Draft ready.");
          } catch {}
          setStreaming(false);
        }
      }, 10000);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lastChunkAt = Date.now();
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === "token") {
              fullScript += evt.content;
              setGeneratedScript(fullScript);
              if (scriptRef.current) {
                scriptRef.current.scrollTop = scriptRef.current.scrollHeight;
              }
            } else if (evt.type === "complete") {
              completedNormally = true;
              clearTimeout(fallbackTimer);
              setScore(evt.score);
              setFeedback(evt.feedback || "");
              setGenerationStatus(
                evt.rewrite_attempts > 0
                  ? `Draft ready after ${evt.rewrite_attempts} rewrite pass${evt.rewrite_attempts > 1 ? "es" : ""}.`
                  : "Draft ready."
              );
              setStreaming(false);
              // Refresh balance
              axios.get(`${API}/credits/balance`, { withCredentials: true })
                .then(r => setBalance(r.data.balance));
              // Show Save Work modal for demo users (once per session, 2s after score appears)
              const isDemo = project?.is_demo;
              if (isDemo && !shownSaveModalRef.current && !sessionStorage.getItem("arcscript_signup_dismissed")) {
                shownSaveModalRef.current = true;
                setTimeout(() => setShowSaveModal(true), 2000);
              }
            } else if (evt.type === "rewrite") {
              setGenerationStatus(evt.message || "ArcEngine is tightening the draft...");
            } else if (evt.type === "error") {
              clearTimeout(fallbackTimer);
              setGenError(evt.message);
              setGenerationStatus("");
              setStreaming(false);
            } else if (evt.type === "validation_error") {
              clearTimeout(fallbackTimer);
              setGenError("Incompatible CE+TS: " + evt.errors.map(e => e.message).join(", "));
              setGenerationStatus("");
              setStreaming(false);
            }
          } catch {}
        }
      }

      clearTimeout(fallbackTimer);

      // Stream ended without complete event — fetch scene as fallback
      if (!completedNormally && fullScript) {
        try {
          const r = await axios.get(`${API}/scenes/${sceneId}`, { withCredentials: true });
          if (r.data.emotional_score) setScore(r.data.emotional_score);
          setGenerationStatus("Draft ready.");
        } catch {}
        setStreaming(false);
      }
    } catch (err) {
      setGenError(err.message);
      setGenerationStatus("");
      setStreaming(false);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    await axios.patch(`${API}/scenes/${sceneId}`, { generated_script: editedScript }, { withCredentials: true });
    setGeneratedScript(editedScript);
    setEditingScript(false);
  };

  const handleRescore = async () => {
    try {
      const r = await axios.post(`${API}/arcengine/score`, { scene_id: sceneId }, { withCredentials: true });
      setScore(r.data.composite_score);
      setFeedback(r.data.feedback);
    } catch {}
  };

  const handleExport = async (format) => {
    if (!generatedScript) return;
    setExportingFormat(format);
    try {
      const response = await axios.post(`${API}/export/scene/${sceneId}?format=${format}`, null, {
        withCredentials: true,
        responseType: "blob",
      });
      const extension = format === "fountain" ? "fountain" : format;
      triggerDownload(response.data, deriveFilename(response.headers, `scene_${sceneId}.${extension}`));
    } catch (error) {
      toast.error(await parseExportError(error), { id: "scene-export-error-toast" });
    } finally {
      setExportingFormat("");
    }
  };

  const ceColor = CE_COLORS[selectedCE] || "var(--accent-gold)";
  const modelInfo = MODEL_OPTIONS.find(m => m.id === selectedModel) || MODEL_OPTIONS[0];
  const isDemo = Boolean(project?.is_demo);
  const readOnly = isDemo && Boolean(scene?.scene_number) && scene.scene_number <= 5;

  if (loading) return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)" }}>
      <TopNav />
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 rounded-full border-2 border-[var(--accent-gold)] border-t-transparent animate-spin" />
      </div>
    </div>
  );

  return (
    <DemoProvider project={project}>
    <div className="min-h-screen flex flex-col" style={{ background: "var(--surface-page)" }}>
      <TopNav />
      <Toaster richColors />

      {/* Thin emotion accent bar */}
      <div className="h-0.5 transition-all duration-500" style={{ background: ceColor, opacity: 0.7 }} />

      <div className="flex flex-1 overflow-hidden" style={{ height: "calc(100vh - 60px)" }}>
        {/* LEFT PANEL — Script (60%) */}
        <div className="flex flex-col border-r overflow-hidden" style={{ flex: "0 0 60%", borderColor: "var(--border-subtle)" }}>
          {/* Scene header */}
          <div className="px-6 py-3 border-b flex items-center gap-4" style={{ borderColor: "var(--border-subtle)" }}>
            <button onClick={() => navigate(`/studio/${projectId}`)}
              className="text-[var(--ink-tertiary)] hover: transition-colors flex-shrink-0">
              <ArrowLeft size={16} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs text-[var(--ink-tertiary)]">
                <span className="font-mono">Scene {scene?.scene_number}</span>
                <span>·</span>
                <span className="truncate">{scene?.location}</span>
              </div>
              {(scene?.brief || scene?.context) && (
                <p className="text-[var(--ink-secondary)] text-xs mt-0.5 line-clamp-1">{scene.brief || scene.context}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowHistory(true)} data-testid="history-button"
                className="flex items-center gap-1.5 text-[var(--ink-tertiary)] hover: text-xs px-2 py-1 rounded-lg transition-colors"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                <History size={12} />
                History
              </button>
            </div>
          </div>

          {/* Script area */}
          <div className="flex-1 overflow-y-auto p-6" ref={scriptRef}>
            {readOnly && (
              <div
                className="rounded-lg px-4 py-2 text-sm text-center mb-4"
                style={{ background: "var(--accent-gold-soft)", color: "var(--accent-ink-dark)" }}
                data-testid="demo-readonly-banner"
              >
                This is a showcase scene. Navigate to Scene 6 to generate your own.
              </div>
            )}
            {!generatedScript && !generating ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <motion.div
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                  className="mb-4"
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: `${ceColor}15`, border: `1px solid ${ceColor}30` }}>
                    <Zap size={28} style={{ color: ceColor }} />
                  </div>
                </motion.div>
                <p className="text-[var(--ink-secondary)] text-base font-medium">Add a brief, set your emotion, then hit Forge</p>
                <p className="text-[var(--ink-tertiary)] text-sm mt-1">Describe the scene first, then tune Core Emotion, Transitional States, and Involuntary Responses →</p>
              </div>
            ) : (
              <div className="max-w-2xl">
                {editingScript ? (
                  <div>
                    <textarea
                      value={editedScript}
                      onChange={e => setEditedScript(e.target.value)}
                      className="w-full screenplay-font text-[var(--ink-primary)] bg-transparent outline-none resize-none"
                      style={{ minHeight: "400px", lineHeight: "1.8" }}
                    />
                    <div className="flex gap-3 mt-4">
                      <button onClick={handleSaveEdit} data-testid="save-edit-button"
                        className="px-4 py-2 rounded-lg text-sm font-medium"
                        style={{ background: "var(--semantic-success)" }}>
                        Save Changes
                      </button>
                      <button onClick={() => setEditingScript(false)}
                        className="px-4 py-2 rounded-lg text-[var(--ink-secondary)] text-sm"
                        style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <pre
                      className={`screenplay-font text-[var(--ink-primary)] whitespace-pre-wrap leading-relaxed ${streaming ? "streaming-cursor" : ""}`}
                      data-testid="generated-script"
                    >
                      {generatedScript}
                    </pre>
                    {!streaming && generatedScript && (
                      <div className="mt-4 flex items-center gap-3">
                        <button
                          onClick={() => { setEditingScript(true); setEditedScript(generatedScript); }}
                          data-testid="edit-script-button"
                          className="text-xs px-3 py-1.5 rounded-lg text-[var(--ink-secondary)] transition-colors"
                          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                          Edit Script
                        </button>
                        <button onClick={handleRescore} data-testid="rescore-button"
                          className="text-xs px-3 py-1.5 rounded-lg text-[var(--ink-secondary)] transition-colors"
                          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                          Re-score
                        </button>
                      </div>
                    )}
                    <ScoreDisplay score={score} feedback={feedback} />
                  </div>
                )}
              </div>
            )}
            {generating && !generatedScript && (
              <div className="mt-4" style={{ animation: "page-enter 0.4s ease forwards" }}>
                <ForgeAnimation ce={selectedCE} />
              </div>
            )}
            {generationStatus && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs"
                data-testid="generation-status-banner"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-secondary)" }}
              >
                <Info size={12} />
                {generationStatus}
              </div>
            )}
          </div>

          {genError && (
            <div className="mx-6 mb-4 p-3 rounded-xl flex items-start gap-2 text-sm"
              style={{ background: "rgba(184,117,107,0.1)", border: "1px solid rgba(184,117,107,0.3)", color: "var(--semantic-error)" }}>
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              {genError}
            </div>
          )}
        </div>

        {/* RIGHT PANEL — Emotion Panel (40%) */}
        <div className="flex flex-col overflow-y-auto" style={{ flex: "0 0 40%", background: "var(--surface-page)" }}>
          <div
            className="h-1 transition-all duration-500"
            style={{ background: `linear-gradient(to right, ${ceColor}, transparent)` }}
          />

          <div className="p-5 space-y-5">
            <ContinuityPanel
              episodeId={episodeId}
              sceneNumber={scene?.scene_number}
            />

            {/* Form fields — disabled when viewing a read-only demo scene */}
            <div style={readOnly ? { pointerEvents: "none", opacity: 0.55 } : {}}>
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm">Scene Brief</h3>
                <span className="text-[var(--ink-tertiary)] text-xs">Required</span>
              </div>
              <textarea
                value={sceneBrief}
                onChange={(e) => setSceneBrief(e.target.value)}
                onBlur={(e) => saveSceneBrief(e.target.value)}
                rows={4}
                required
                data-testid="scene-brief-textarea"
                placeholder="Describe what happens in this scene: setting, characters present, key beat, emotional intent..."
                className="w-full px-3 py-3 rounded-xl text-sm outline-none resize-none"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }}
              />
            </div>

            {/* 1. Core Emotion Grid */}
            <div>
              <h3 className="font-bold text-sm mb-3">Core Emotion</h3>
              <div className="grid grid-cols-3 gap-2">
                {CE_DATA.map((ce, i) => {
                  const color = CE_COLORS[ce.code];
                  const isSelected = selectedCE === ce.code;
                  return (
                    <motion.button
                      key={ce.code}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => handleCESelect(ce.code)}
                      data-testid={`emotion-chip-${ce.code.toLowerCase()}`}
                      className="p-2.5 rounded-xl text-left transition-all duration-200"
                      style={{
                        background: isSelected ? `${color}20` : "var(--surface-card)",
                        border: `1px solid ${isSelected ? color : "var(--border-subtle)"}`,
                        boxShadow: isSelected ? `0 0 12px ${color}30` : "none",
                      }}
                    >
                      <div className="w-2 h-2 rounded-full mb-1.5" style={{ background: color }} />
                      <div className="text-xs font-semibold leading-tight">{ce.name}</div>
                      <div className="text-[var(--ink-tertiary)] text-xs mt-0.5 leading-tight hidden lg:block">{ce.desc}</div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-sm">Secondary Core Emotion</h3>
                  <span className="text-[var(--ink-tertiary)] text-xs">Optional hybrid layer</span>
                </div>
                <select
                  value={secondaryCE}
                  onChange={(e) => handleSecondaryCEChange(e.target.value)}
                  data-testid="secondary-core-emotion-select"
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                  style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }}
                >
                  <option value="">None (single-emotion scene)</option>
                  {CE_DATA.filter((ce) => ce.code !== selectedCE).map((ce) => (
                    <option key={ce.code} value={ce.code}>{`${ce.code} — ${ce.name}`}</option>
                  ))}
                </select>
                <p className="text-[var(--ink-tertiary)] text-xs mt-2">For hybrid scenes like revenge-with-grief or love-with-fear.</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-sm">Scene Type</h3>
                  <span className="text-[var(--ink-tertiary)] text-xs">Staging layer</span>
                </div>
                <select
                  value={sceneType}
                  onChange={(e) => handleSceneTypeChange(e.target.value)}
                  data-testid="scene-type-select"
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                  style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }}
                >
                  <option value="">Select scene type...</option>
                  {SCENE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className="text-[var(--ink-tertiary)] text-xs mt-2">A Fury scene can still be staged as confrontation, solitude, discovery, decision, or transition.</p>
              </div>
            </div>

            {/* 2. Transitional States */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm">Emotional Undercurrents</h3>
                <span className="text-[var(--ink-tertiary)] text-xs">{selectedTS.length}/3 selected</span>
              </div>
              <p className="text-[var(--ink-tertiary)] text-xs mb-3">What simmers beneath the surface</p>
              <div className="flex flex-wrap gap-2">
                {tsOptions.length === 0 ? (
                  <span className="text-[var(--ink-tertiary)] text-xs">Select an emotion above to see compatible states</span>
                ) : (
                  tsOptions.map(ts => {
                    const isSelected = selectedTS.includes(ts.code);
                    const isCaution = ts.compatibility === "caution";
                    return (
                      <button
                        key={ts.code}
                        onClick={() => toggleTS(ts.code)}
                        data-testid={`ts-chip-${ts.code.toLowerCase()}`}
                        title={ts.description}
                        className="text-xs px-2.5 py-1 rounded-full transition-all duration-150 flex items-center gap-1"
                        style={{
                          background: isSelected ? `${ceColor}20` : "var(--surface-card)",
                          border: `1px solid ${isSelected ? ceColor : "var(--border-subtle)"}`,
                          color: isSelected ? ceColor : "var(--ink-secondary)",
                        }}
                      >
                        {isCaution && <span>⚠</span>}
                        {ts.name}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* 3. Involuntary Responses */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm">Physical Tells</h3>
                <span className="text-[var(--ink-tertiary)] text-xs">{selectedIR.length}/3 selected</span>
              </div>
              <p className="text-[var(--ink-tertiary)] text-xs mb-3">How the body betrays the feeling</p>
              <div className="flex flex-wrap gap-2">
                {irOptions.map(ir => {
                  const isSelected = selectedIR.includes(ir.code);
                  const isBestWith = (ir.best_with_ce || []).includes(selectedCE);
                  return (
                    <button
                      key={ir.code}
                      onClick={() => toggleIR(ir.code)}
                      data-testid={`ir-chip-${ir.code.toLowerCase()}`}
                      title={ir.description}
                      className="text-xs px-2.5 py-1 rounded-full transition-all duration-150 flex items-center gap-1"
                      style={{
                        background: isSelected ? `${ceColor}20` : "var(--surface-card)",
                        border: `1px solid ${isSelected ? ceColor : "var(--border-subtle)"}`,
                        color: isSelected ? ceColor : "var(--ink-secondary)",
                      }}
                    >
                      {isBestWith && <Star size={9} className="text-[var(--semantic-warning)]" />}
                      {ir.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 4. Model Selector */}
            <div>
              <h3 className="font-bold text-sm mb-3">AI Model</h3>
              <div className="space-y-2">
                {MODEL_OPTIONS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedModel(m.id)}
                    data-testid={`model-option-${m.id}`}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all"
                    style={{
                      background: selectedModel === m.id ? "var(--surface-card)" : "transparent",
                      border: `1px solid ${selectedModel === m.id ? "var(--accent-gold)" : "var(--border-subtle)"}`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {selectedModel === m.id && <Check size={12} className="text-[var(--accent-gold)]" />}
                      <span className="text-xs font-medium">{m.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full"
                        style={{ background: `${m.badgeColor}20`, color: m.badgeColor }}>
                        {m.badge}
                      </span>
                    </div>
                    <span className="text-[var(--ink-tertiary)] text-xs">{m.cost} cr</span>
                  </button>
                ))}
              </div>

              <div className="mt-3 px-3 py-2 rounded-xl text-xs"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                <span className="text-[var(--ink-tertiary)]">Cost: </span>
                <span className="font-semibold">{modelInfo.cost} credits</span>
                <span className="text-[var(--ink-tertiary)]"> · Balance: </span>
                <span className="text-[var(--accent-gold)] font-semibold">{balance}</span>
              </div>
            </div>

            {/* 5. Additional Direction */}
            <div>
              <h3 className="font-bold text-sm mb-2">Additional Direction</h3>
              <textarea
                value={additionalDirection}
                onChange={e => setAdditionalDirection(e.target.value)}
                placeholder="Any specific direction for this scene? (optional)"
                rows={2}
                data-testid="additional-direction-input"
                className="w-full px-3 py-2.5 rounded-xl text-[var(--ink-secondary)] text-sm outline-none resize-none"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
              />
            </div>
            </div>{/* end read-only wrapper */}

            {/* FORGE BUTTON */}
            <div className="flex items-stretch gap-3" data-testid="scene-editor-export-actions-row">
              <ExportMenu
                disabled={!generatedScript}
                exporting={Boolean(exportingFormat)}
                onExport={handleExport}
                testIdPrefix="scene-export-menu"
              />
              {!readOnly && (
              <motion.button
                onClick={handleGenerate}
                disabled={generating || !sceneType || !sceneBrief.trim()}
                aria-disabled={generating || !sceneType || !sceneBrief.trim()}
                tabIndex={generating || !sceneType || !sceneBrief.trim() ? -1 : 0}
                type="button"
                data-testid="forge-generate-button"
                className={`flex-1 py-4 rounded-xl font-black text-base tracking-wide transition-all duration-300 flex items-center justify-center gap-3 ${!generating && sceneType && sceneBrief.trim() ? "gold-pulse" : ""}`}
                animate={!generating && sceneType && sceneBrief.trim() ? {
                  boxShadow: [
                    `0 0 20px ${ceColor}30`,
                    `0 0 40px ${ceColor}50`,
                    `0 0 20px ${ceColor}30`,
                  ]
                } : {}}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  background: generating || !sceneType || !sceneBrief.trim() ? "var(--surface-card)" : ceColor,
                  border: generating || !sceneType || !sceneBrief.trim() ? `1px solid var(--border-subtle)` : "none",
                  cursor: generating || !sceneType || !sceneBrief.trim() ? "not-allowed" : "pointer",
                  opacity: sceneType && sceneBrief.trim() ? 1 : 0.75,
                  pointerEvents: generating || !sceneType || !sceneBrief.trim() ? "none" : "auto",
                }}
              >
                {generating ? (
                  <>
                    <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    Forging...
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    FORGE THIS SCENE
                  </>
                )}
              </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showHistory && (
        <HistoryModal
          sceneId={sceneId}
          onClose={(restored) => {
            setShowHistory(false);
            if (restored) {
              axios.get(`${API}/scenes/${sceneId}`, { withCredentials: true })
                .then(r => {
                  setGeneratedScript(r.data.generated_script || "");
                  setScore(r.data.emotional_score);
                });
            }
          }}
        />
      )}

      {showSaveModal && (
        <SaveWorkModal
          userEmail={user?.email}
          onSave={() => {
            setShowSaveModal(false);
            toast.success("Account saved! You can now log in anytime.");
          }}
          onSkip={() => {
            setShowSaveModal(false);
            sessionStorage.setItem("arcscript_signup_dismissed", "true");
          }}
        />
      )}
    </div>
    </DemoProvider>
  );
}
